import { useEffect, useState, useCallback } from 'react'
import { boxApi } from '../utils/api'

const ACTION_LABELS = {
  'member.updated': 'Member updated',
  'member.promoted': 'Member promoted to organizer',
  'member.removed': 'Member removed',
  'ledger.created': 'Ledger entry added',
  'ledger.voided': 'Ledger entry voided',
}

function formatDetails(action, details = {}) {
  switch (action) {
    case 'member.updated':
      return `Fields: ${details.changes?.join(', ') || '—'}`
    case 'member.promoted':
      return 'New organizer assigned'
    case 'member.removed':
      return `Reason: ${details.reason || '—'}`
    case 'ledger.voided':
      return `Reason: ${details.reason || '—'}`
    case 'ledger.created':
      return `${details.type || ''} • $${details.amount || ''}`
    default:
      return JSON.stringify(details)
  }
}

export default function AuditLog({ boxId }) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true)
      const data = await boxApi.getAuditLogs(boxId)
      setLogs(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-gray-500">Loading audit log...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex items-start justify-between">
          <p className="text-red-800">Error loading audit log: {error}</p>
          <button
            onClick={loadLogs}
            className="text-sm font-medium text-red-700 underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center text-gray-500">
        No activity yet. Admin actions will show up here.
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit log</h3>
          <p className="text-sm text-gray-500">A history of member and ledger changes.</p>
        </div>
        <button
          onClick={loadLogs}
          className="text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actor
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {ACTION_LABELS[log.action] || log.action}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDetails(log.action, log.details)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {log.actor_email || log.actor_id || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
